import { css, Theme } from '@emotion/react';
import { Google } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useAppAuth } from 'providers';
import { ReactNode } from 'react';

import { AuthProviderName } from '../types';

interface SocialButtonProps {
  provider: AuthProviderName;
  name: string;
  icon: ReactNode;
}

const SOCIAL_BUTTONS: SocialButtonProps[] = [
  {
    provider: AuthProviderName.Google,
    name: 'Login with Google',
    icon: <Google />,
  },
];

const SOCIAL_BUTTONS_COLORS: Record<AuthProviderName, string> = {
  [AuthProviderName.Google]: '#4285F4',
  [AuthProviderName.Facebook]: '#2C4288',
  [AuthProviderName.LinkedIn]: '#0077B7',
};

/**
 * Renders a list of social buttons for the given providers.
 * @throws {Error} When outside of the `<AuthProvider />` component.
 * @returns A list of social buttons for the given providers.
 */
export function SocialButtons() {
  const { socialSignIn } = useAppAuth();

  return (
    <Box css={socialButtonsContainerCSS}>
      {SOCIAL_BUTTONS.map(item => (
        <Button
          key={item.provider}
          size="large"
          variant="contained"
          startIcon={item.icon}
          onClick={() => socialSignIn(item.provider)}
          css={socialButtonCSS(item.provider)}
          disableElevation
        >
          {item.name}
        </Button>
      ))}
    </Box>
  );
}

const socialButtonsContainerCSS = css`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const socialButtonCSS = (provider: AuthProviderName) => (theme: Theme) => {
  const backgroundColor = SOCIAL_BUTTONS_COLORS[provider];
  const textColor = theme.palette.getContrastText(backgroundColor);

  return css`
    background-color: ${backgroundColor};
    color: ${textColor};

    &:hover {
      background-color: ${backgroundColor};
    }
  `;
};
